import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import { fetchTask, updateTask, fetchJob } from "../../api/companyApi";
import { fetchFunctionsByRoles } from "../../api/marketApi";
import { useNotification } from "../../providers/NotificationProvider";

export default function TaskEditPage() {
  const { businessDomainId, jobId, taskId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const lang = navigator.language;

  const [original, setOriginal] = useState(null);
  const [form, setForm] = useState({
    taskName: "",
    taskDescription: "",
    url: "",
  });
  const [selectedFunctionId, setSelectedFunctionId] = useState("");
  const [functions, setFunctions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [d, jobData] = await Promise.all([
          fetchTask(taskId),
          fetchJob(jobId),
        ]);
        setOriginal(d);
        setForm({
          taskName: d.taskName || "",
          taskDescription: d.taskDescription || "",
          url: d.url || "",
        });
        if (d.functionId) {
          setSelectedFunctionId(String(d.functionId));
        }
        if (jobData?.roles && Array.isArray(jobData.roles) && jobData.roles.length > 0) {
          const funcs = await fetchFunctionsByRoles(jobData.roles);
          setFunctions(Array.isArray(funcs) ? funcs : []);
        }
      } catch (e) {
        notify.apiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, jobId]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const func = functions.find((f) => String(f.id) === selectedFunctionId) || null;
    try {
      await updateTask({
        ...original,
        taskName: form.taskName,
        taskDescription: form.taskDescription,
        url: form.url,
        functionId: func ? func.id : original?.functionId,
        functionName: func
          ? (lang === "ja" ? func.japaneseName : func.englishName)
          : original?.functionName,
      });
      notify.success(t("message.update.success"));
      navigate(`/design/${businessDomainId}/jobs/${jobId}/tasks/${taskId}`);
    } catch (err) {
      notify.apiError(err);
    }
  };

  if (!original) return null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("task.edit.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth
              required
              multiline
              label={t("task.name")}
              name="taskName"
              value={form.taskName}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t("task.description")}
              name="taskDescription"
              value={form.taskDescription}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label={t("common.url")}
              name="url"
              value={form.url}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("task.function")}</InputLabel>
              <Select
                value={selectedFunctionId}
                onChange={(e) => setSelectedFunctionId(e.target.value)}
                label={t("task.function")}
                renderValue={(value) => {
                  const func = functions.find((f) => String(f.id) === value);
                  if (func) return lang === "ja" ? func.japaneseName : func.englishName;
                  return original?.functionName || "";
                }}
              >
                {functions.map((func) => (
                  <MenuItem key={func.id} value={String(func.id)}>
                    {lang === "ja" ? func.japaneseName : func.englishName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">
            {t("common.save")}
          </Button>
          <Button variant="outlined" onClick={() => navigate(`/design/${businessDomainId}/jobs/${jobId}/tasks/${taskId}`)}>
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
